import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["trackId", "lessonId", "contentId", "language"];
import { Component } from 'react';
import PortalIdParser from 'PortalIdParser';
import UsageTracker from '../../../utils/UsageTracker';
import { TrackingContentTypes } from '../../../constants/Tracking';
import NotStartedPracticalExercise from './NotStartedPracticalExercise';
import InProgressPracticalExercise from './InProgressPracticalExercise';
import PracticalExerciseRequiredProduct from './PracticalExerciseRequiredProduct';
import PracticalExerciseRequiredAccess from './PracticalExerciseRequiredAccess';
import PracticalExerciseLoading from './PracticalExerciseLoading';
import PracticalExerciseError from './PracticalExerciseError';
import { ContentProgressTypes, PracticalExerciseTypes } from '../../../constants/ContentConstants';
import PracticalExerciseWithSubmission from './PracticalExerciseWithSubmission';
import InReviewPracticalExerciseCondensed from './InReviewPracticalExerciseCondensed';
import CompletedPracticalExerciseCondensed from './CompletedPracticalExerciseCondensed';
import FailedPracticalExerciseCondensed from './FailedPracticalExerciseCondensed';
import { useContentStatus } from '../../userProgress/userProgressQueries';
import { useDeletePracticalExerciseProgress, usePracticalExercise, usePracticalExerciseRequirements, useSetManuallyGradedPracticalExerciseSubmitted, useSetManuallyGradedPracticalExerciseWithAssetSubmitted, useSetPracticalExerciseStarted } from '../practicalExerciseQueries';
import { useIsSimulationExercise } from '../practicalExerciseQueries';
import Raven from 'raven-js';
// @ts-expect-error no types
import SafeStorage from 'SafeStorage';
import { ACADEMY_EXERCISE_CONTEXT_KEY } from 'navigation-components/config';
import getLang from '../../../utils/getLang';
import { jsx as _jsx } from "react/jsx-runtime";
class PracticalExercise extends Component {
  constructor(...args) {
    super(...args);
    this.track = action => {
      const {
        practicalExercise,
        practicalExerciseRequirements,
        contentId,
        trackId,
        trackName,
        lessonId,
        lessonName
      } = this.props;
      const graded = practicalExercise === null || practicalExercise === void 0 ? void 0 : practicalExercise.graded;
      const manuallyApproved = practicalExercise === null || practicalExercise === void 0 ? void 0 : practicalExercise.manuallyApproved;
      const name = practicalExercise === null || practicalExercise === void 0 ? void 0 : practicalExercise.name;
      const trackerNotRequired = practicalExercise === null || practicalExercise === void 0 ? void 0 : practicalExercise.trackerNotRequired;
      const requiredProduct = practicalExerciseRequirements && practicalExerciseRequirements.requiredProducts && practicalExerciseRequirements.requiredProducts[0];
      const trackingMethod = trackerNotRequired ? UsageTracker.track : UsageTracker.trackExternalLink;
      trackingMethod.call(UsageTracker, 'contentInteraction', {
        action,
        contentId,
        contentLanguage: practicalExercise === null || practicalExercise === void 0 ? void 0 : practicalExercise.language,
        contentType: TrackingContentTypes.PRACTICAL_EXERCISE_FLOW,
        trackId,
        trackName,
        lessonId,
        lessonName,
        contentName: name,
        productName: requiredProduct || '',
        isUntrackedExercise: !!trackerNotRequired,
        graded: !!graded,
        manuallyApproved: !!manuallyApproved
      });
    };
    this.handleClickThroughToProductPage = () => {
      this.track('click-to-product-page');
    };
    this.handleSkipScreenClickSkip = () => {
      const {
        onFinish
      } = this.props;
      this.track('click-skip-screen-skip');
      onFinish();
    };
    this.handleSkipScreenClickRetry = () => {
      this.track('click-skip-screen-retry');
    };
    this.setExerciseContext = simulationPortalId => {
      const {
        practicalExercise,
        contentId,
        lessonId,
        trackId,
        isSimulation
      } = this.props;
      if (!practicalExercise) return;
      try {
        SafeStorage.setItem(ACADEMY_EXERCISE_CONTEXT_KEY, JSON.stringify({
          name: practicalExercise.name,
          description: practicalExercise.description,
          id: contentId,
          exerciseLocation: practicalExercise.exerciseLocation || '',
          lessonId,
          trackId,
          portalId: simulationPortalId || PortalIdParser.getPortalIdFromPath(),
          sourcePortalId: PortalIdParser.getPortalIdFromPath(),
          isSimulation,
          isGraded: practicalExercise.graded,
          isManuallyGraded: practicalExercise.manuallyApproved
        }));
      } catch (error) {
        Raven.captureException(error);
      }
    };
    this.handleStartExercise = async simulationPortalId => {
      const {
        practicalExercise,
        onStartExercise,
        trackId,
        lessonId,
        contentId
      } = this.props;
      const isExternal = practicalExercise === null || practicalExercise === void 0 ? void 0 : practicalExercise.trackerNotRequired;
      this.setExerciseContext(simulationPortalId);
      this.track('click-start-exercise');
      if (isExternal) {
        await onStartExercise(trackId && Number(trackId), Number(lessonId), Number(contentId), simulationPortalId);
      }
    };
    this.handleResumeExercise = simulationPortalId => {
      this.setExerciseContext(simulationPortalId);
      this.track('click-resume-exercise');
    };
    this.handleReviewExercise = () => {
      this.track('click-review-exercise');
      const {
        practicalExercise,
        onExerciseFinished,
        contentId,
        trackId,
        lessonId
      } = this.props;
      const language = practicalExercise === null || practicalExercise === void 0 ? void 0 : practicalExercise.language;
      onExerciseFinished(Number(contentId), language, Number(trackId) || undefined, Number(lessonId) || undefined);
    };
    this.handleSubmitManualExercise = (assetType, reference) => {
      this.track('click-review-exercise');
      const {
        onSubmitWithAsset,
        contentId
      } = this.props;
      onSubmitWithAsset(Number(contentId), assetType, reference);
    };
    this.handleClickResubmit = () => {
      this.track('retry-practical-exercise');
      this.props.onRetryPracticalExercise(Number(this.props.contentId));
    };
  }
  render() {
    const {
      onFinish,
      contentId,
      practicalExerciseStatus,
      practicalExerciseStatusError,
      practicalExerciseStatusLoading,
      lessonId,
      trackId,
      practicalExercise,
      practicalExerciseLoading,
      practicalExerciseError,
      practicalExerciseRequirements,
      practicalExerciseRequirementsLoading,
      practicalExerciseRequirementsError
    } = this.props;
    const requiredProduct = practicalExerciseRequirements && practicalExerciseRequirements.requiredProducts && practicalExerciseRequirements.requiredProducts[0];
    const requiredAccess = practicalExerciseRequirements && practicalExerciseRequirements.requiredAccess && practicalExerciseRequirements.requiredAccess[0];
    const isManual = practicalExercise && practicalExercise.manuallyApproved;
    const error = practicalExerciseError || practicalExerciseRequirementsError || practicalExerciseStatusError;
    const loading = practicalExerciseLoading || practicalExerciseRequirementsLoading || practicalExerciseStatusLoading;
    const practicalExerciseType = practicalExercise && practicalExercise.type;
    if (loading) {
      return /*#__PURE__*/_jsx(PracticalExerciseLoading, {});
    }
    if (error) {
      return /*#__PURE__*/_jsx(PracticalExerciseError, {
        error: error
      });
    }
    if (practicalExerciseStatus === ContentProgressTypes.COMPLETED) {
      return /*#__PURE__*/_jsx(CompletedPracticalExerciseCondensed, {
        advanceToNextContent: onFinish,
        contentId: contentId
      });
    }
    if (practicalExerciseStatus === ContentProgressTypes.IN_REVIEW) {
      return /*#__PURE__*/_jsx(InReviewPracticalExerciseCondensed, {
        advanceToNextContent: onFinish,
        contentId: contentId,
        isManual: !!isManual,
        isPassRequiredForCert: !!(practicalExercise !== null && practicalExercise !== void 0 && practicalExercise.graded),
        lessonId: lessonId,
        onResubmitClick: this.handleClickResubmit,
        resubmitType: 'resubmit',
        title: (practicalExercise === null || practicalExercise === void 0 ? void 0 : practicalExercise.name) || '',
        trackId: trackId
      });
    }
    if (practicalExerciseStatus === ContentProgressTypes.STARTED && practicalExerciseType !== PracticalExerciseTypes.MANUALLY_GRADED_WITH_FILE_SUBMISSION) {
      return /*#__PURE__*/_jsx(InProgressPracticalExercise, {
        advanceToNextContent: onFinish,
        contentId: contentId,
        exercise: practicalExercise,
        exerciseLocation: (practicalExercise === null || practicalExercise === void 0 ? void 0 : practicalExercise.exerciseLocation) || '',
        language: (practicalExercise === null || practicalExercise === void 0 ? void 0 : practicalExercise.language) || getLang(),
        lessonId: lessonId,
        onResumeExercise: this.handleResumeExercise,
        trackId: trackId
      });
    }
    if (practicalExerciseStatus === ContentProgressTypes.FAILED) {
      return /*#__PURE__*/_jsx(FailedPracticalExerciseCondensed, {
        advanceToNextContent: onFinish,
        contentId: contentId,
        isPassRequiredForCert: !!(practicalExercise !== null && practicalExercise !== void 0 && practicalExercise.graded),
        lessonId: lessonId,
        onResubmitClick: this.handleClickResubmit,
        resubmitType: 'resubmit',
        title: (practicalExercise === null || practicalExercise === void 0 ? void 0 : practicalExercise.name) || '',
        trackId: trackId
      });
    }
    if (requiredProduct) {
      return /*#__PURE__*/_jsx(PracticalExerciseRequiredProduct, {
        advanceToNextContent: onFinish,
        onClickProductLink: this.handleClickThroughToProductPage,
        portalId: PortalIdParser.getPortalIdFromPath(),
        requiredProduct: requiredProduct
      });
    }
    if (requiredAccess) {
      return /*#__PURE__*/_jsx(PracticalExerciseRequiredAccess, {
        advanceToNextContent: onFinish,
        product: requiredAccess
      });
    }
    if (practicalExerciseType === PracticalExerciseTypes.MANUALLY_GRADED_WITH_FILE_SUBMISSION) {
      return /*#__PURE__*/_jsx(PracticalExerciseWithSubmission, {
        advanceToNextContent: onFinish,
        isPassRequiredForCert: !!(practicalExercise !== null && practicalExercise !== void 0 && practicalExercise.graded),
        isSubmitting: false,
        onSubmit: this.handleSubmitManualExercise,
        title: (practicalExercise === null || practicalExercise === void 0 ? void 0 : practicalExercise.name) || ''
      });
    }
    return practicalExercise && /*#__PURE__*/_jsx(NotStartedPracticalExercise, {
      advanceToNextContent: onFinish,
      contentId: Number(contentId),
      exercise: practicalExercise,
      exerciseLocation: practicalExercise.exerciseLocation || '',
      language: practicalExercise.language,
      lessonId: Number(lessonId),
      onStartClick: this.handleStartExercise,
      trackId: Number(trackId)
    });
  }
}
const PracticalExerciseFunctionalContainer = _ref => {
  let {
      trackId,
      lessonId,
      contentId,
      language
    } = _ref,
    props = _objectWithoutPropertiesLoose(_ref, _excluded);
  const {
    practicalExercise,
    practicalExerciseLoading,
    practicalExerciseError
  } = usePracticalExercise(Number(lessonId), Number(contentId), language);
  const {
    practicalExerciseRequirements,
    practicalExerciseRequirementsLoading,
    practicalExerciseRequirementsError
  } = usePracticalExerciseRequirements(Number(contentId));
  const {
    contentStatus,
    contentStatusLoading,
    contentStatusError
  } = useContentStatus(Number(contentId), language);
  const [startPracticalExercise] = useSetPracticalExerciseStarted(language);
  const [submitPracticalExerciseWithAsset] = useSetManuallyGradedPracticalExerciseWithAssetSubmitted(language);
  const [submitManuallyGradedPracticalExercise] = useSetManuallyGradedPracticalExerciseSubmitted(language);
  const [deletePracticalExerciseProgress] = useDeletePracticalExerciseProgress();
  const {
    isSimulationExercise
  } = useIsSimulationExercise(Number(contentId));
  const handleOnExerciseFinished = (pContentId, pLanguage, pTrackId, pLessonId) => {
    submitManuallyGradedPracticalExercise(pContentId, pLanguage, pTrackId, pLessonId).catch(Raven.captureException.bind(Raven));
  };
  const handleOnRetryPracticalExercise = pContentId => {
    deletePracticalExerciseProgress(pContentId).catch(Raven.captureException.bind(Raven));
  };
  const handleOnStartExercise = async (pTrackId, pLessonId, pContentId, simulationPortalId) => {
    await startPracticalExercise(pTrackId, pLessonId, pContentId, simulationPortalId).catch(Raven.captureException.bind(Raven));
  };
  const handleonSubmitWithAsset = (pContentId, pAssetType, pReference) => {
    submitPracticalExerciseWithAsset(pContentId, pAssetType, pReference).catch(Raven.captureException);
  };
  return /*#__PURE__*/_jsx(PracticalExercise, Object.assign({
    contentId: contentId,
    isSimulation: isSimulationExercise,
    lessonId: lessonId,
    onExerciseFinished: handleOnExerciseFinished,
    onRetryPracticalExercise: handleOnRetryPracticalExercise,
    onStartExercise: handleOnStartExercise,
    onSubmitWithAsset: handleonSubmitWithAsset,
    practicalExercise: practicalExercise,
    practicalExerciseError: practicalExerciseError,
    practicalExerciseLoading: practicalExerciseLoading,
    practicalExerciseRequirements: practicalExerciseRequirements,
    practicalExerciseRequirementsError: practicalExerciseRequirementsError,
    practicalExerciseRequirementsLoading: practicalExerciseRequirementsLoading,
    practicalExerciseStatus: contentStatus,
    practicalExerciseStatusError: contentStatusError,
    practicalExerciseStatusLoading: contentStatusLoading,
    trackId: trackId
  }, props));
};
export const WEBPACK_3_FORCE_MODULE_IMPORT = 1;
export default PracticalExerciseFunctionalContainer;